/**
 * Materiali preferiti — per utente nell'org corrente.
 *
 * useFavoriteMaterials(): Set di material id preferiti
 * useToggleFavoriteMaterial(): mutation che aggiunge/rimuove un materiale dai preferiti
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';

export const useFavoriteMaterials = () => {
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();

  const query = useQuery({
    queryKey: ['favorite-materials', user?.id, currentOrganizationId],
    enabled: !!user && !!currentOrganizationId,
    staleTime: 60_000,
    queryFn: async (): Promise<string[]> => {
      if (!user || !currentOrganizationId) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('user_material_favorites')
        .select('material_id')
        .eq('user_id', user.id)
        .eq('organization_id', currentOrganizationId);
      if (error) {
        console.error('[useFavoriteMaterials] error:', error);
        return [];
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data ?? []).map((r: any) => r.material_id);
    },
  });

  const favoritesSet = useMemo(() => new Set(query.data ?? []), [query.data]);

  return {
    favoritesSet,
    favoritesIds: query.data ?? [],
    isLoading: query.isLoading,
  };
};

export const useToggleFavoriteMaterial = () => {
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ materialId, isFavorite }: { materialId: string; isFavorite: boolean }) => {
      if (!user || !currentOrganizationId) throw new Error('Sessione non valida');
      if (isFavorite) {
        // Remove
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from('user_material_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('organization_id', currentOrganizationId)
          .eq('material_id', materialId);
        if (error) throw error;
      } else {
        // Add
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from('user_material_favorites')
          .insert({ user_id: user.id, organization_id: currentOrganizationId, material_id: materialId });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorite-materials', user?.id, currentOrganizationId] });
    },
  });
};
